import { useEffect, useState } from 'react'
import { Link, useLocation, useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CURRENT_BUILD_ID } from '@/lib/app-version'
import {
  componentStackFor,
  describeError,
  errorIdFor,
  isChunkLoadError,
  projectIdFromPath,
} from '@/lib/client-error-report'
import { cn } from '@/lib/utils'

/**
 * What a render crash shows instead of TanStack Router's bare "Something went
 * wrong!" (board uvu5). `RouteErrorPage` is the router's default error
 * component: a view that throws is replaced inside the project layout, so the
 * sidebar and navbar stay. `AppErrorPage` is the same panel filling the
 * screen, for the root and the project layout itself (no chrome left to keep).
 *
 * Logging and the report to the Worker happen in the router's `onCatch`
 * (`handleCaughtRenderError`), not here — this only renders.
 */
export function RouteErrorPage(props: ErrorComponentProps) {
  return <ErrorPanel {...props} className="min-h-0 flex-1" />
}

export function AppErrorPage(props: ErrorComponentProps) {
  return <ErrorPanel {...props} className="bg-background h-dvh" />
}

function ErrorPanel({ error, reset, className }: ErrorComponentProps & { className?: string }) {
  const { t } = useTranslation()
  const router = useRouter()
  const { pathname } = useLocation()
  const projectId = projectIdFromPath(pathname)
  const errorId = errorIdFor(error)
  const staleBuild = isChunkLoadError(error)
  const { name, message, stack } = describeError(error)
  // The component stack arrives in the boundary's componentDidCatch, which
  // runs after this first render.
  const [componentStack, setComponentStack] = useState<string | null>(null)
  useEffect(() => setComponentStack(componentStackFor(error)), [error])

  const tryAgain = () => {
    // Clear the boundary and re-run the matches — what a reload did before,
    // without losing the rest of the app.
    reset()
    void router.invalidate()
  }

  const details = [
    `${t('errorPage.errorId')}: ${errorId}`,
    `${t('errorPage.route')}: ${pathname}`,
    `${t('errorPage.version')}: ${CURRENT_BUILD_ID}`,
    '',
    // V8 stacks start with the "Name: message" line; others (Safari) do not.
    stack?.startsWith(`${name}: ${message}`) ? stack : `${name}: ${message}\n${stack ?? ''}`,
    componentStack ? `\n${t('errorPage.componentStack')}:${componentStack}` : '',
  ]
    .join('\n')
    .trim()

  return (
    <div
      role="alert"
      data-slot="route-error"
      className={cn('flex w-full items-center justify-center overflow-y-auto p-6', className)}
    >
      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center">
        <span className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-full">
          <TriangleAlert className="size-6" aria-hidden />
        </span>
        <div className="flex flex-col gap-1.5">
          <h1 className="text-lg font-semibold">{t('errorPage.title')}</h1>
          <p className="text-muted-foreground text-sm">
            {staleBuild ? t('errorPage.staleBody') : t('errorPage.body')}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {staleBuild ? (
            <Button onClick={() => window.location.reload()}>{t('errorPage.reload')}</Button>
          ) : (
            <Button onClick={tryAgain}>{t('errorPage.tryAgain')}</Button>
          )}
          <Button asChild variant="outline">
            {projectId ? (
              <Link to="/$projectId" params={{ projectId }} onClick={() => reset()}>
                {t('errorPage.goToProject')}
              </Link>
            ) : (
              <Link to="/" onClick={() => reset()}>
                {t('errorPage.goHome')}
              </Link>
            )}
          </Button>
        </div>
        {!staleBuild && (
          <p className="text-muted-foreground text-xs">{t('errorPage.persistHint')}</p>
        )}
        <details className="border-border w-full rounded-lg border text-left text-xs">
          <summary className="text-muted-foreground cursor-pointer px-3 py-2 select-none">
            {t('errorPage.technicalDetails')}
          </summary>
          <div className="border-border flex flex-col gap-2 border-t px-3 py-2">
            <p>
              {t('errorPage.errorId')}: <code className="font-mono">{errorId}</code>
            </p>
            <pre className="bg-muted max-h-64 overflow-auto rounded p-2 font-mono text-[11px] whitespace-pre-wrap break-words">
              {details}
            </pre>
          </div>
        </details>
      </div>
    </div>
  )
}
